import { useEffect, useRef } from 'react';
import { useToastQueue } from "../ToastQueueProvider";

/**
 * Pauses toast dismiss when anything inside the container is focused
 *
 * Note: This code can probably be simplified after upgrading to react 17.
 * @see: https://github.com/facebook/react/issues/6410#issuecomment-671499208
 */
export function usePauseToastDismissTimersOnFocus(_ref) {
  var containerRef = _ref.containerRef;
  var toastQueue = useToastQueue();
  useEffect(function () {
    var container = containerRef.current;
    if (!container) {
      return;
    }
    function onFocusIn() {
      toastQueue.pauseAllToastDismissTimers();
    }
    function onFocusOut(e) {
      var stillInsideContainer = container && e.relatedTarget instanceof Element && container.contains(e.relatedTarget);
      if (!stillInsideContainer) {
        toastQueue.resumeAllToastDismissTimers();
      } else {
        toastQueue.pauseAllToastDismissTimers();
      }
    }
    container.addEventListener('focusin', onFocusIn);
    container.addEventListener('focusout', onFocusOut);
    return function () {
      container.removeEventListener('focusin', onFocusIn);
      container.removeEventListener('focusout', onFocusOut);
    };
  }, [containerRef, toastQueue]);
}

/**
 * Need to make sure focus is never lost when dismissing toasts:
 * https://stackoverflow.com/a/63849350
 *
 * Otherwise we may violate WCAG 2.3.7 if the focus is not visible
 *
 * Interestingly, it seems like radix toasts violate this
 */
export function useRescueFocusOnToastDismiss(_ref2) {
  var containerRef = _ref2.containerRef;
  var toastQueue = useToastQueue();
  var lastFocusedElRef = useLastFocusedElBeforeFocusingToasts({
    containerRef: containerRef
  });
  useEffect(function () {
    return toastQueue.onHide(function (toastId) {
      var toastEl = document.getElementById(toastId);
      if (!toastEl) return;
      var aboutToRemoveFocusedEl = Boolean(toastEl && toastEl.contains(document.activeElement));
      if (!aboutToRemoveFocusedEl) return;
      var closeButtonWithinNextToast = getNextEnabledCloseButton(toastEl);
      var closeButtonWithinPrevToast = getPrevEnabledCloseButton(toastEl);
      if (toastQueue.areAllToastsHidden()) {
        var _lastFocusedElRef$cur;
        (_lastFocusedElRef$cur = lastFocusedElRef.current) === null || _lastFocusedElRef$cur === void 0 ? void 0 : _lastFocusedElRef$cur.focus();
      } else if (closeButtonWithinNextToast) {
        closeButtonWithinNextToast.focus();
      } else {
        closeButtonWithinPrevToast === null || closeButtonWithinPrevToast === void 0 ? void 0 : closeButtonWithinPrevToast.focus();
      }
    });
  }, [toastQueue, lastFocusedElRef, containerRef]);
}

/**
 * Returns the last element that was focused before the user focused an element
 * inside of the toasts.  This can be useful for focus management for
 * accessibility purposes
 */
function useLastFocusedElBeforeFocusingToasts(_ref3) {
  var containerRef = _ref3.containerRef;
  var lastFocusedElRef = useRef(null);
  useEffect(function () {
    var container = containerRef.current;
    if (!container) {
      return;
    }
    function onFocusIn(e) {
      if (isProgrammaticallyFocusable(e.target) && containerRef.current && e.target instanceof Element && !containerRef.current.contains(e.target)) {
        lastFocusedElRef.current = e.target;
      }
    }
    window.document.addEventListener('focusin', onFocusIn);
    return function () {
      window.document.removeEventListener('focusin', onFocusIn);
    };
  }, [containerRef]);
  return lastFocusedElRef;
}
function isProgrammaticallyFocusable(item) {
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  return Boolean(item && typeof item.focus === 'function');
}
function getPrevEnabledCloseButton(toastEl) {
  var _closeButtonWithinPre2;
  var prevToast = toastEl;
  var closeButtonWithinPrevToast = null;
  do {
    var _prevToast, _prevToast2, _closeButtonWithinPre;
    prevToast = (_prevToast = prevToast) === null || _prevToast === void 0 ? void 0 : _prevToast.previousElementSibling;
    closeButtonWithinPrevToast = (_prevToast2 = prevToast) === null || _prevToast2 === void 0 ? void 0 : _prevToast2.querySelector('button');
  } while (prevToast && (_closeButtonWithinPre = closeButtonWithinPrevToast) !== null && _closeButtonWithinPre !== void 0 && _closeButtonWithinPre.disabled);
  if ((_closeButtonWithinPre2 = closeButtonWithinPrevToast) !== null && _closeButtonWithinPre2 !== void 0 && _closeButtonWithinPre2.disabled) {
    return null;
  }
  return closeButtonWithinPrevToast;
}
function getNextEnabledCloseButton(toastEl) {
  var _closeButtonWithinNex2;
  var nextToast = toastEl;
  var closeButtonWithinNextToast = null;
  do {
    var _nextToast, _nextToast2, _closeButtonWithinNex;
    nextToast = (_nextToast = nextToast) === null || _nextToast === void 0 ? void 0 : _nextToast.nextElementSibling;
    closeButtonWithinNextToast = (_nextToast2 = nextToast) === null || _nextToast2 === void 0 ? void 0 : _nextToast2.querySelector('button');
  } while (nextToast && (_closeButtonWithinNex = closeButtonWithinNextToast) !== null && _closeButtonWithinNex !== void 0 && _closeButtonWithinNex.disabled);
  if ((_closeButtonWithinNex2 = closeButtonWithinNextToast) !== null && _closeButtonWithinNex2 !== void 0 && _closeButtonWithinNex2.disabled) {
    return null;
  }
  return closeButtonWithinNextToast;
}